/**
 * Mensajes generales — campañas de correo a empresas (acordeones).
 * Correos desde ficha del cliente (máx. 3), editables; no toca Robot SAT.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Loader2, Plus, RefreshCw, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DespachoSectionCard } from "@/components/despacho/DespachoUi";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  despachoMensajesGeneralesApi,
  parseMensajesGeneralesError,
} from "@/modules/despacho/despachoMensajesGeneralesApi";
import { despachoFiscalInfoApi } from "@/modules/despacho/despachoFiscalInfoApi";
import { emailTransportStatusLabel } from "@/lib/smtpPresets";

const STATUS_LABEL = {
  pending: "Pendiente",
  queued: "En cola",
  sent: "Enviado",
  failed: "Falló",
  skipped: "Omitido",
  omitted: "No enviar",
};

const MESSAGE_KIND_OPTIONS = [
  { value: "recordatorio", label: "Recordatorio" },
  { value: "envio_documentacion", label: "Envío de documentación" },
];

function messageKindLabel(kind) {
  return MESSAGE_KIND_OPTIONS.find((o) => o.value === kind)?.label
    || (kind === "envio_documentacion" ? "Envío de documentación" : "Recordatorio");
}

function emptySlots() {
  return [
    { address: "", selected: false, valid: false },
    { address: "", selected: false, valid: false },
    { address: "", selected: false, valid: false },
  ];
}

function TaskDocsPicker({
  docs,
  loading,
  selectedTaskIds = [],
  selectedMonthIds = [],
  onChange,
  onRefresh,
  testId = "mensaje-task-docs-picker",
}) {
  const selectedTask = new Set((selectedTaskIds || []).map(String));
  const selectedMonth = new Set((selectedMonthIds || []).map(String));
  const selected = new Set([...selectedTask, ...selectedMonth]);
  const toggle = (doc) => {
    const id = String(doc.id);
    const isMonth = doc.source === "month" || Boolean(doc.bucket);
    const taskNext = new Set(selectedTask);
    const monthNext = new Set(selectedMonth);
    if (isMonth) {
      if (monthNext.has(id)) monthNext.delete(id);
      else monthNext.add(id);
    } else {
      if (taskNext.has(id)) taskNext.delete(id);
      else taskNext.add(id);
    }
    onChange({
      taskIds: Array.from(taskNext),
      monthIds: Array.from(monthNext),
    });
  };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <Label>Documentos listos para el cliente</Label>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 rounded-sm px-2 text-[11px]"
          onClick={() => onRefresh?.()}
        >
          <RefreshCw className="h-3.5 w-3.5 mr-1" aria-hidden />
          Actualizar
        </Button>
      </div>
      <p className="text-[10px] text-slate-500 m-0">
        Preferente: <strong>Seguimiento mensual → Documentos del mes</strong> (declaraciones / contable).
        También desde Tareas. Al enviar, cada empresa solo recibe sus archivos. Auxiliar: sus empresas;
        dueño/comprador: todas.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-600 py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          Cargando documentos listos…
        </div>
      ) : null}
      {!loading && !(docs || []).length ? (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-sm px-2 py-1.5 m-0">
          No hay documentos listos. Cárguelos en Seguimiento (documentos del mes) o en Tareas → Detalle.
        </p>
      ) : null}
      {(docs || []).length ? (
        <ul className="max-h-44 overflow-y-auto rounded-sm border border-slate-200 divide-y divide-slate-100">
          {docs.map((d) => {
            const id = String(d.id);
            const checked = selected.has(id);
            return (
              <li key={id} className="flex items-start gap-2 px-2 py-1.5 text-xs hover:bg-slate-50">
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(d)}
                  className="mt-0.5"
                  aria-label={`Adjuntar ${d.title || d.filename}`}
                />
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => toggle(d)}
                >
                  <span className="font-medium text-[#0F2D5C] block truncate">
                    {d.title || d.filename}
                  </span>
                  <span className="text-slate-500 block">
                    {[d.source_label || (d.source === "month" ? "Mes" : "Tarea"),
                      d.bucket_label, d.doc_kind_label, d.client_name,
                      d.period_key || d.period, d.assigned_to_name]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      {selected.size ? (
        <p className="text-[10px] text-slate-600 m-0">{selected.size} documento(s) vinculados a este mensaje.</p>
      ) : null}
    </div>
  );
}

function MessageAccordion({
  message,
  readOnly,
  open,
  onToggle,
  onReloadList,
}) {
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [detail, setDetail] = useState(message);
  const [busyId, setBusyId] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [readyDocs, setReadyDocs] = useState([]);
  const [readyDocsLoading, setReadyDocsLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    title: message.title || "",
    body: message.body || "",
    scheduled_at: (message.scheduled_at || "").slice(0, 16),
    attachment_name: message.attachment_name || "",
    attachment_note: message.attachment_note || "",
    message_kind: message.message_kind || "recordatorio",
    task_document_ids: Array.isArray(message.task_document_ids)
      ? message.task_document_ids.map(String)
      : [],
    month_document_ids: Array.isArray(message.month_document_ids)
      ? message.month_document_ids.map(String)
      : [],
  });

  const loadReadyDocs = useCallback(async () => {
    setReadyDocsLoading(true);
    try {
      const [taskItems, monthItems] = await Promise.all([
        despachoFiscalInfoApi.listReadyTaskDocuments().catch(() => []),
        despachoFiscalInfoApi.listReadyMonthClientDocuments().catch(() => []),
      ]);
      const tasks = (Array.isArray(taskItems) ? taskItems : []).map((d) => ({
        ...d,
        source: d.source || "task",
        source_label: "Tarea",
      }));
      const months = (Array.isArray(monthItems) ? monthItems : []).map((d) => ({
        ...d,
        source: "month",
        source_label: "Mes",
      }));
      setReadyDocs([...months, ...tasks]);
    } catch {
      setReadyDocs([]);
    } finally {
      setReadyDocsLoading(false);
    }
  }, []);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const data = await despachoMensajesGeneralesApi.get(message.id);
      setDetail(data.item || message);
      setRecipients(data.recipients || []);
      setEditForm({
        title: data.item?.title || "",
        body: data.item?.body || "",
        scheduled_at: String(data.item?.scheduled_at || "").slice(0, 16),
        attachment_name: data.item?.attachment_name || "",
        attachment_note: data.item?.attachment_note || "",
        message_kind: data.item?.message_kind || "recordatorio",
        task_document_ids: Array.isArray(data.item?.task_document_ids)
          ? data.item.task_document_ids.map(String)
          : [],
        month_document_ids: Array.isArray(data.item?.month_document_ids)
          ? data.item.month_document_ids.map(String)
          : [],
      });
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    if (open) loadDetail();
  }, [open, loadDetail]);

  useEffect(() => {
    if (editOpen) void loadReadyDocs();
  }, [editOpen, loadReadyDocs]);

  const patchRecipient = async (row, patch, { persist } = {}) => {
    if (readOnly || !row?.id) return;
    setBusyId(row.id);
    try {
      const updated = await despachoMensajesGeneralesApi.patchRecipient(message.id, row.id, {
        ...patch,
        persist_emails_to_client: Boolean(persist),
      });
      setRecipients((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
      if (persist) toast.success("Correos guardados en la ficha del cliente.");
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const handleSaveMessage = async () => {
    if (readOnly) return;
    try {
      const item = await despachoMensajesGeneralesApi.update(message.id, {
        title: editForm.title.trim(),
        body: editForm.body,
        scheduled_at: editForm.scheduled_at || null,
        attachment_name: editForm.attachment_name,
        attachment_note: editForm.attachment_note,
        message_kind: editForm.message_kind || "recordatorio",
        task_document_ids: editForm.task_document_ids || [],
        month_document_ids: editForm.month_document_ids || [],
        status: editForm.scheduled_at ? "scheduled" : "draft",
      });
      setDetail(item);
      setEditOpen(false);
      toast.success("Mensaje actualizado.");
      onReloadList?.();
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    }
  };

  const handleDelete = async () => {
    if (readOnly) return;
    if (!window.confirm(`¿Eliminar el mensaje «${detail.title}» y sus selecciones?`)) return;
    try {
      await despachoMensajesGeneralesApi.remove(message.id);
      toast.success("Mensaje eliminado.");
      onReloadList?.();
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    }
  };

  const handleDispatch = async ({ dryRun = false, forceImmediate = false } = {}) => {
    if (readOnly) return;
    setBusyId(forceImmediate ? "dispatch-now" : "dispatch");
    try {
      const data = await despachoMensajesGeneralesApi.dispatch(message.id, {
        only_selected: true,
        dry_run: dryRun,
        force_immediate: forceImmediate,
      });
      if (data.scheduled_only) {
        toast.message(data.message || "Mensaje programado para envío automático.");
        await loadDetail();
        onReloadList?.();
        return;
      }
      const s = data.summary || {};
      if (s.sent) {
        toast.success(`Enviados: ${s.sent}. Fallidos: ${s.failed || 0}. En cola: ${s.queued || 0}.`);
      } else if (s.queued) {
        toast.message(
          `Quedaron ${s.queued} en cola. Complete Parámetros de correo; el servidor reintentará automáticamente.`,
        );
      } else if (!s.workspace_enabled) {
        toast.error("Active «Permitir envíos a clientes» en Parámetros de correo.");
      } else {
        toast.message(`Procesados: ${s.total || 0}. Revise estados por empresa.`);
      }
      await loadDetail();
      onReloadList?.();
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const handleResend = async (row) => {
    if (readOnly || !row?.id) return;
    setBusyId(row.id);
    try {
      const data = await despachoMensajesGeneralesApi.resend(message.id, row.id);
      if (data.ok) toast.success("Reenviado.");
      else toast.message(data.result?.detail || "No se pudo reenviar (revise configuración).");
      await loadDetail();
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const handleResendFailed = async () => {
    if (readOnly) return;
    const failed = recipients.filter((r) => r.selected && (r.send_status === "failed" || r.send_status === "queued"));
    if (!failed.length) {
      toast.message("No hay filas seleccionadas fallidas o en cola para reenviar.");
      return;
    }
    setBusyId("resend-failed");
    let ok = 0;
    try {
      for (const row of failed) {
        const data = await despachoMensajesGeneralesApi.resend(message.id, row.id);
        if (data.ok) ok += 1;
      }
      toast.success(`Reenvíos ok: ${ok} de ${failed.length}.`);
      await loadDetail();
      onReloadList?.();
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const handleRefresh = async () => {
    if (readOnly) return;
    try {
      const data = await despachoMensajesGeneralesApi.refreshRecipients(message.id);
      toast.success(`Empresas nuevas agregadas: ${data.added || 0}.`);
      await loadDetail();
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    }
  };

  const scheduledLabel = (detail.scheduled_at || "").replace("T", " ").slice(0, 16) || "Sin fecha";
  const scheduledAtMs = detail.scheduled_at ? Date.parse(String(detail.scheduled_at)) : NaN;
  const hasFutureSchedule = Number.isFinite(scheduledAtMs) && scheduledAtMs > Date.now();
  const selectedCount = message.recipients_selected ?? detail.recipients_selected ?? "—";
  const totalCount = message.recipients_total ?? detail.recipients_total ?? "—";
  const sentCount = message.recipients_sent ?? detail.recipients_sent ?? 0;

  return (
    <section
      className={`rounded-sm border bg-white ${open ? "border-[#0F2D5C]/40 shadow-sm" : "border-slate-200"}`}
      data-testid={`mensaje-general-${message.id}`}
    >
      {/* Cabecera del listado: título + fecha + acciones (como el wireframe) */}
      <div className="flex w-full items-start gap-2 px-3 py-2.5 hover:bg-slate-50">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
          aria-expanded={open}
          onClick={onToggle}
          data-testid={`mensaje-general-toggle-${message.id}`}
        >
          <ChevronDown
            className={`mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
            aria-hidden
          />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-[#0F2D5C]">
              {detail.title || "Sin título"}
              <span className="ml-2 inline-block rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 align-middle">
                {detail.message_kind_label || messageKindLabel(detail.message_kind)}
              </span>
            </span>
            <span className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
              <span>
                Programa: <strong className="font-medium text-slate-700">{scheduledLabel}</strong>
              </span>
              <span>
                Empresas: {selectedCount}/{totalCount}
              </span>
              <span>Enviados: {sentCount}</span>
              {((detail.task_document_ids || []).length + (detail.month_document_ids || []).length) ? (
                <span>
                  Docs: {(detail.month_document_ids || []).length + (detail.task_document_ids || []).length}
                </span>
              ) : null}
            </span>
          </span>
        </button>
        {!readOnly ? (
          <div className="flex shrink-0 flex-wrap items-center gap-1 pt-0.5">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 rounded-sm px-2 text-[11px]"
              onClick={(e) => {
                e.stopPropagation();
                setEditOpen(true);
              }}
              data-testid={`mensaje-general-edit-${message.id}`}
            >
              Editar
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 rounded-sm px-2 text-[11px] text-red-700"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              data-testid={`mensaje-general-delete-${message.id}`}
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
              <span className="sr-only">Eliminar</span>
            </Button>
          </div>
        ) : null}
      </div>

      {open ? (
        <div className="border-t border-slate-100 p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {!readOnly ? (
              <>
                <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={handleRefresh}>
                  <RefreshCw className="h-3.5 w-3.5 mr-1" aria-hidden />
                  Actualizar empresas
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  onClick={() => setEditOpen(true)}
                >
                  Adjuntar / documento
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="rounded-sm bg-[#0F2D5C]"
                  disabled={busyId === "dispatch" || busyId === "dispatch-now"}
                  onClick={() => handleDispatch({ dryRun: false, forceImmediate: false })}
                  data-testid="mensaje-general-dispatch"
                >
                  {busyId === "dispatch" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                  {hasFutureSchedule ? "Programar envío" : "Enviar"}
                </Button>
                {hasFutureSchedule ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    disabled={busyId === "dispatch" || busyId === "dispatch-now"}
                    onClick={() => handleDispatch({ dryRun: false, forceImmediate: true })}
                    data-testid="mensaje-general-dispatch-now"
                  >
                    {busyId === "dispatch-now" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    Enviar ahora
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-sm"
                  disabled={busyId === "resend-failed"}
                  onClick={handleResendFailed}
                  data-testid="mensaje-general-resend-failed"
                >
                  {busyId === "resend-failed" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                  Reenviar fallidos
                </Button>
              </>
            ) : null}
          </div>

          {(detail.attachment_name || detail.attachment_note) ? (
            <p className="text-xs text-slate-600 m-0">
              Documento de referencia: <strong>{detail.attachment_name || "—"}</strong>
              {detail.attachment_note ? ` — ${detail.attachment_note}` : ""}
            </p>
          ) : null}
          {((detail.month_document_ids || []).length || (detail.task_document_ids || []).length) ? (
            <p className="text-xs text-emerald-800 m-0" data-testid="mensaje-general-task-docs-linked">
              Adjuntos: mes {(detail.month_document_ids || []).length} · tareas {(detail.task_document_ids || []).length}
              {" "}(se filtran por empresa al enviar).
            </p>
          ) : null}

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Cargando empresas…
            </div>
          ) : (
            <div className="overflow-x-auto rounded-sm border border-slate-200">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-2 py-2 text-left font-medium">Enviar</th>
                    <th className="px-2 py-2 text-left font-medium">Empresa</th>
                    <th className="px-2 py-2 text-left font-medium">Auxiliar</th>
                    <th className="px-2 py-2 text-left font-medium">Correos (máx. 3)</th>
                    <th className="px-2 py-2 text-left font-medium">Estado</th>
                    <th className="px-2 py-2 text-left font-medium">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-3 py-4 text-slate-500">
                        No hay empresas en este mensaje. Use «Actualizar empresas».
                      </td>
                    </tr>
                  ) : (
                    recipients.map((row) => {
                      const slots = row.email_slots?.length ? row.email_slots : emptySlots();
                      return (
                        <tr key={row.id} className="border-t border-slate-100 align-top">
                          <td className="px-2 py-2">
                            <Checkbox
                              checked={Boolean(row.selected)}
                              disabled={readOnly || busyId === row.id}
                              onCheckedChange={(v) => patchRecipient(row, { selected: Boolean(v) })}
                              data-testid={`mensaje-general-select-${row.fiscal_client_id}`}
                            />
                          </td>
                          <td className="px-2 py-2">
                            <div className="font-medium text-slate-800">{row.client_name}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{row.client_rfc}</div>
                          </td>
                          <td className="px-2 py-2 text-slate-700">{row.auxiliar_name || "—"}</td>
                          <td className="px-2 py-2 min-w-[16rem]">
                            <div className="space-y-1">
                              {slots.slice(0, 3).map((slot, idx) => (
                                <div key={`${row.id}-em-${idx}`} className="flex items-center gap-1">
                                  <Checkbox
                                    checked={Boolean(slot.selected)}
                                    disabled={readOnly || busyId === row.id || !slot.address}
                                    onCheckedChange={(v) => {
                                      const next = slots.map((s, i) =>
                                        i === idx ? { ...s, selected: Boolean(v) } : s,
                                      );
                                      patchRecipient(row, { email_slots: next });
                                    }}
                                  />
                                  <Input
                                    className="h-7 text-xs font-mono"
                                    value={slot.address || ""}
                                    disabled={readOnly || busyId === row.id}
                                    placeholder={`correo ${idx + 1}`}
                                    onChange={(e) => {
                                      const next = slots.map((s, i) =>
                                        i === idx
                                          ? { ...s, address: e.target.value, selected: Boolean(e.target.value.trim()) }
                                          : s,
                                      );
                                      setRecipients((prev) =>
                                        prev.map((r) => (r.id === row.id ? { ...r, email_slots: next } : r)),
                                      );
                                    }}
                                    onBlur={(e) => {
                                      const next = slots.map((s, i) =>
                                        i === idx
                                          ? { ...s, address: e.target.value, selected: Boolean(e.target.value.trim()) }
                                          : s,
                                      );
                                      patchRecipient(row, { email_slots: next });
                                    }}
                                  />
                                </div>
                              ))}
                              {!readOnly ? (
                                <button
                                  type="button"
                                  className="text-[10px] text-[#0F2D5C] underline"
                                  onClick={() => patchRecipient(row, { email_slots: slots }, { persist: true })}
                                >
                                  Guardar correos en cliente
                                </button>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-2 py-2">
                            <span className="text-slate-700">{STATUS_LABEL[row.send_status] || row.send_status}</span>
                            {row.last_error ? (
                              <div className="text-[10px] text-amber-700 mt-0.5 max-w-[12rem]">{row.last_error}</div>
                            ) : null}
                          </td>
                          <td className="px-2 py-2">
                            {!readOnly ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="rounded-sm h-7 text-[11px]"
                                disabled={busyId === row.id}
                                onClick={() => handleResend(row)}
                              >
                                Reenviar
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-sm max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar mensaje general</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo de campaña</Label>
              <Select
                value={editForm.message_kind || "recordatorio"}
                onValueChange={(v) => setEditForm((f) => ({ ...f, message_kind: v }))}
              >
                <SelectTrigger className="mt-1 rounded-sm" data-testid="mensaje-edit-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MESSAGE_KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nombre del mensaje</Label>
              <Input
                className="mt-1"
                value={editForm.title}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              />
            </div>
            <div>
              <Label>Fecha de programación</Label>
              <Input
                type="datetime-local"
                className="mt-1"
                value={editForm.scheduled_at}
                onChange={(e) => setEditForm((f) => ({ ...f, scheduled_at: e.target.value }))}
              />
            </div>
            <div>
              <Label>Texto del correo</Label>
              <SpellcheckTextarea
                className="mt-1 min-h-[120px]"
                value={editForm.body}
                onChange={(e) => setEditForm((f) => ({ ...f, body: e.target.value }))}
              />
            </div>
            <TaskDocsPicker
              docs={readyDocs}
              loading={readyDocsLoading}
              selectedTaskIds={editForm.task_document_ids}
              selectedMonthIds={editForm.month_document_ids}
              onRefresh={loadReadyDocs}
              onChange={({ taskIds, monthIds }) =>
                setEditForm((f) => ({
                  ...f,
                  task_document_ids: taskIds,
                  month_document_ids: monthIds,
                }))
              }
              testId="mensaje-edit-task-docs"
            />
            <div>
              <Label>Nota de referencia (texto en el correo)</Label>
              <Input
                className="mt-1"
                placeholder="Ej. Declaración ISR mayo — opcional"
                value={editForm.attachment_name}
                onChange={(e) => setEditForm((f) => ({ ...f, attachment_name: e.target.value }))}
              />
              <Input
                className="mt-2"
                placeholder="Nota adicional"
                value={editForm.attachment_note}
                onChange={(e) => setEditForm((f) => ({ ...f, attachment_note: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setEditOpen(false)}>
              Cancelar
            </Button>
            <Button type="button" className="rounded-sm bg-[#0F2D5C]" onClick={handleSaveMessage}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default function CalendarioMensajesGeneralesPanel({
  readOnly = false,
  onOpenParams,
  onOpenPlantillas,
}) {
  const [items, setItems] = useState([]);
  const [emailConfig, setEmailConfig] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templateQuery, setTemplateQuery] = useState("");
  const [loadState, setLoadState] = useState("loading");
  const [openId, setOpenId] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [readyDocs, setReadyDocs] = useState([]);
  const [readyDocsLoading, setReadyDocsLoading] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");
  const [form, setForm] = useState({
    template_id: "",
    title: "",
    body: "",
    scheduled_at: "",
    attachment_name: "",
    attachment_note: "",
    message_kind: "recordatorio",
    task_document_ids: [],
    month_document_ids: [],
  });

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const data = await despachoMensajesGeneralesApi.list({
        messageKind: kindFilter === "all" ? undefined : kindFilter,
      });
      setItems(data.items || []);
      setEmailConfig(data.email_config || null);
      setLoadState("ready");
    } catch (err) {
      setLoadState("error");
      toast.error(parseMensajesGeneralesError(err).message);
    }
  }, [kindFilter]);

  const loadReadyDocs = useCallback(async () => {
    setReadyDocsLoading(true);
    try {
      const [taskItems, monthItems] = await Promise.all([
        despachoFiscalInfoApi.listReadyTaskDocuments().catch(() => []),
        despachoFiscalInfoApi.listReadyMonthClientDocuments().catch(() => []),
      ]);
      const tasks = (Array.isArray(taskItems) ? taskItems : []).map((d) => ({
        ...d,
        source: d.source || "task",
        source_label: "Tarea",
      }));
      const months = (Array.isArray(monthItems) ? monthItems : []).map((d) => ({
        ...d,
        source: "month",
        source_label: "Mes",
      }));
      setReadyDocs([...months, ...tasks]);
    } catch {
      setReadyDocs([]);
    } finally {
      setReadyDocsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadTemplates = useCallback(async (kind) => {
    try {
      const data = await despachoMensajesGeneralesApi.listTemplates({
        activeOnly: true,
        messageKind: kind || undefined,
      });
      setTemplates(data.items || []);
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    }
  }, []);

  const filteredTemplates = useMemo(() => {
    const needle = templateQuery.trim().toLowerCase();
    if (!needle) return templates;
    return templates.filter(
      (t) =>
        String(t.name || "").toLowerCase().includes(needle) ||
        String(t.body || "").toLowerCase().includes(needle),
    );
  }, [templates, templateQuery]);

  const applyTemplate = (templateId) => {
    const tpl = templates.find((t) => t.id === templateId);
    setForm((f) => ({
      ...f,
      template_id: templateId || "",
      title: tpl?.name || f.title,
      body: tpl?.body || f.body,
      message_kind: tpl?.message_kind || f.message_kind || "recordatorio",
    }));
  };

  const openCreate = async () => {
    const initialKind = kindFilter === "all" ? "recordatorio" : kindFilter;
    setForm({
      template_id: "",
      title: "",
      body: "",
      scheduled_at: "",
      attachment_name: "",
      attachment_note: "",
      message_kind: initialKind,
      task_document_ids: [],
      month_document_ids: [],
    });
    setTemplateQuery("");
    setCreateOpen(true);
    await Promise.all([loadTemplates(initialKind), loadReadyDocs()]);
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error("Indique el nombre del mensaje o elija una plantilla.");
      return;
    }
    setCreating(true);
    try {
      const data = await despachoMensajesGeneralesApi.create({
        title: form.title.trim(),
        body: form.body,
        scheduled_at: form.scheduled_at || null,
        attachment_name: form.attachment_name,
        attachment_note: form.attachment_note,
        message_kind: form.message_kind || "recordatorio",
        task_document_ids: form.task_document_ids || [],
        month_document_ids: form.month_document_ids || [],
        template_id: form.template_id || null,
        seed_recipients: true,
        selected_by_default: true,
      });
      toast.success(`Mensaje creado (${data.recipients_created || 0} empresas). Revise empresas y envíe.`);
      setCreateOpen(false);
      setForm({
        template_id: "",
        title: "",
        body: "",
        scheduled_at: "",
        attachment_name: "",
        attachment_note: "",
        task_document_ids: [],
      });
      await load();
      if (data.item?.id) setOpenId(data.item.id);
    } catch (err) {
      toast.error(parseMensajesGeneralesError(err).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="calendario-mensajes-generales">
      <DespachoSectionCard title="Correo de salida del despacho">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-slate-700">
            Remitente: <strong>{emailConfig?.from_email || "No configurado"}</strong>
            {emailConfig?.from_name ? ` (${emailConfig.from_name})` : ""}
          </span>
          <span className={`text-xs ${emailConfig?.enabled ? "text-emerald-700" : "text-amber-700"}`}>
            {emailConfig?.enabled ? "Envíos permitidos" : "Envíos desactivados"}
          </span>
          <span className={`text-xs ${emailConfig?.transport_ready ? "text-emerald-700" : "text-amber-700"}`}>
            {emailTransportStatusLabel(emailConfig)}
          </span>
          {!readOnly ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm"
              onClick={() => (onOpenParams ? onOpenParams() : null)}
              data-testid="mensaje-general-go-params"
            >
              Ir a Parámetros de correo
            </Button>
          ) : null}
        </div>
      </DespachoSectionCard>

      <div className="flex flex-wrap gap-2 items-center" data-testid="mensaje-general-kind-filter">
        {[
          { id: "all", label: "Todos" },
          ...MESSAGE_KIND_OPTIONS.map((o) => ({ id: o.value, label: o.label })),
        ].map((opt) => (
          <Button
            key={opt.id}
            type="button"
            size="sm"
            variant={kindFilter === opt.id ? "default" : "outline"}
            className={`rounded-sm h-8 text-xs ${kindFilter === opt.id ? "bg-[#0F2D5C]" : ""}`}
            onClick={() => setKindFilter(opt.id)}
            data-testid={`mensaje-kind-filter-${opt.id}`}
          >
            {opt.label}
          </Button>
        ))}
        {!readOnly ? (
          <Button
            type="button"
            size="sm"
            className="rounded-sm bg-[#0F2D5C]"
            onClick={openCreate}
            data-testid="mensaje-general-crear"
          >
            <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
            Nuevo mensaje
          </Button>
        ) : null}
        <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={load}>
          Actualizar lista
        </Button>
      </div>

      {loadState === "loading" ? (
        <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Cargando mensajes…
        </div>
      ) : null}

      {loadState === "ready" && items.length === 0 ? (
        <DespachoSectionCard title="Listado de mensajes">
          <p className="text-sm text-slate-600 m-0" data-testid="mensaje-general-lista-vacia">
            Aún no hay mensajes. Pulse <strong>Nuevo mensaje</strong>, elija una plantilla y cree la campaña del
            periodo.
          </p>
        </DespachoSectionCard>
      ) : null}

      {items.length > 0 ? (
        <DespachoSectionCard title={`Listado de mensajes (${items.length})`}>
          <p className="text-xs text-slate-500 m-0 mb-3">
            Cada fila es un mensaje. Pulse la flecha para desplegar empresas, fecha y envío.
          </p>
          <div className="space-y-2" data-testid="mensaje-general-lista-acordeones">
            {items.map((msg) => (
              <MessageAccordion
                key={msg.id}
                message={msg}
                readOnly={readOnly}
                open={openId === msg.id}
                onToggle={() => setOpenId((cur) => (cur === msg.id ? "" : msg.id))}
                onReloadList={load}
              />
            ))}
          </div>
        </DespachoSectionCard>
      ) : null}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-sm max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuevo mensaje general</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo de campaña</Label>
              <Select
                value={form.message_kind || "recordatorio"}
                onValueChange={(v) => {
                  setForm((f) => ({ ...f, message_kind: v, template_id: "" }));
                  void loadTemplates(v);
                }}
              >
                <SelectTrigger className="mt-1 rounded-sm" data-testid="mensaje-create-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MESSAGE_KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1 m-0">
                {form.message_kind === "envio_documentacion"
                  ? "Ideal para adjuntar declaraciones/contable del mes. Asunto y texto personalizables; firma y pie en Parámetros."
                  : "Avisos para pedir papelería u otra información. Firma y pie en Parámetros."}
              </p>
            </div>
            <div>
              <Label>Plantilla</Label>
              <Input
                className="mt-1"
                placeholder="Buscar por nombre…"
                value={templateQuery}
                onChange={(e) => setTemplateQuery(e.target.value)}
                data-testid="mensaje-general-plantilla-buscar"
              />
              <Select
                value={form.template_id || "_none"}
                onValueChange={(v) => applyTemplate(v === "_none" ? "" : v)}
              >
                <SelectTrigger className="mt-2 rounded-sm" data-testid="mensaje-general-plantilla-select">
                  <SelectValue placeholder="Seleccione plantilla" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Sin plantilla (escribir a mano)</SelectItem>
                  {filteredTemplates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name}
                      {t.recurrence_label ? ` · ${t.recurrence_label}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-500 mt-1 m-0">
                Gestione el catálogo en la pestaña Plantillas.{" "}
                <button type="button" className="underline text-[#0F2D5C]" onClick={() => onOpenPlantillas?.()}>
                  Abrir Plantillas
                </button>
              </p>
            </div>
            <div>
              <Label>Nombre (título del acordeón)</Label>
              <Input
                className="mt-1"
                placeholder="Ej. Recordatorio de papelería"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                data-testid="mensaje-general-title"
              />
            </div>
            <div>
              <Label>Fecha de programación</Label>
              <Input
                type="datetime-local"
                className="mt-1"
                value={form.scheduled_at}
                onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
              />
              <p className="text-[10px] text-slate-500 mt-1 m-0">
                Se muestra en el listado. Confirme el envío en el acordeón (el auxiliar elige empresas).
              </p>
            </div>
            <div>
              <Label>Texto del correo</Label>
              <SpellcheckTextarea
                className="mt-1 min-h-[100px]"
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              />
            </div>
            <TaskDocsPicker
              docs={readyDocs}
              loading={readyDocsLoading}
              selectedTaskIds={form.task_document_ids}
              selectedMonthIds={form.month_document_ids}
              onRefresh={loadReadyDocs}
              onChange={({ taskIds, monthIds }) =>
                setForm((f) => ({
                  ...f,
                  task_document_ids: taskIds,
                  month_document_ids: monthIds,
                }))
              }
              testId="mensaje-create-task-docs"
            />
            <div>
              <Label>Nota de referencia (opcional)</Label>
              <Input
                className="mt-1"
                placeholder="Texto corto en el cuerpo del correo"
                value={form.attachment_name}
                onChange={(e) => setForm((f) => ({ ...f, attachment_name: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              type="button"
              className="rounded-sm bg-[#0F2D5C]"
              disabled={creating}
              onClick={handleCreate}
              data-testid="mensaje-general-create-submit"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}